import React, { useEffect, useState } from "react";
import PageHeader from "../../../common/pageheader/pageheader";
import AGTable from "../../../common/table/AGTable";
import api from "../../../../resources/api";
import Loader from "../../../common/loader/loader";
import { currencyConverter } from "../../../../resources/constants";
import { connect } from "react-redux";

function UniversityWide() {
    const [isLoading, setIsLoading] = useState(true);
    const [invoiceTotals, setInvoiceTotals] = useState({});
    const [paymentTotals, setPaymentTotals] = useState({});
    const [semesterRows, setSemesterRows] = useState([]);
    const [datatable, setDatatable] = useState({
        columns: [
            { label: "Semester", field: "SemesterCode" },
            { label: "Semester Name", field: "SemesterName" },
            { label: "Students", field: "Students" },
            { label: "Total Billed", field: "TotalBilled" },
            { label: "Total Collected", field: "TotalCollected" },
            { label: "Outstanding", field: "Outstanding" },
            { label: "Collection Rate", field: "CollectionRate" },
        ],
        rows: [],
    });

    const getReportData = async () => {
        setIsLoading(true);
        const result = await api.get("staff/finance/invoices/university-wide");
        if (result.success && result.data?.data) {
            const { invoiceTotals: inv, paymentTotals: pay, bySemester } = result.data.data;
            setInvoiceTotals(inv || {});
            setPaymentTotals(pay || {});
            setSemesterRows(bySemester || []);
            buildTable(bySemester || []);
        }
        setIsLoading(false);
    };

    const buildTable = (data) => {
        const rows = data.map((item) => {
            const rate = item.TotalBilled > 0
                ? ((parseFloat(item.TotalCollected) / parseFloat(item.TotalBilled)) * 100).toFixed(1)
                : "0.0";
            return {
                SemesterCode: <span className="badge badge-light-primary">{item.SemesterCode}</span>,
                SemesterName: item.SemesterName || item.SemesterCode,
                Students: item.Students,
                TotalBilled: <span>{currencyConverter(item.TotalBilled)}</span>,
                TotalCollected: <span className="text-success fw-bold">{currencyConverter(item.TotalCollected)}</span>,
                Outstanding: <span className="text-danger fw-bold">{currencyConverter(item.Outstanding)}</span>,
                CollectionRate: (
                    <div className="d-flex align-items-center gap-2">
                        <div className="progress flex-grow-1" style={{ height: 8 }}>
                            <div className="progress-bar bg-success" style={{ width: `${rate}%` }}></div>
                        </div>
                        <span className="fw-bold text-nowrap">{rate}%</span>
                    </div>
                ),
            };
        });
        setDatatable(prev => ({ ...prev, rows }));
    };

    const exportToCSV = () => {
        if (!semesterRows.length) return;
        const headers = ["Semester Code", "Semester Name", "Students", "Total Billed", "Total Collected", "Outstanding"];
        const rows = semesterRows.map(item => [
            item.SemesterCode, item.SemesterName || item.SemesterCode,
            item.Students, item.TotalBilled, item.TotalCollected, item.Outstanding,
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `university_wide_report_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };

    const collectionRate = () => {
        const billed = parseFloat(invoiceTotals.GrandTotalBilled) || 0;
        const collected = parseFloat(invoiceTotals.GrandTotalCollected) || 0;
        return billed > 0 ? ((collected / billed) * 100).toFixed(1) : "0.0";
    };

    useEffect(() => { getReportData(); }, []);

    if (isLoading) return <Loader />;

    return (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title="University-Wide Finance Summary" items={["Finance", "Invoices", "University Wide"]} />
            <div className="flex-column-fluid">

                {/* Grand Invoice Totals */}
                <div className="row g-4 mb-4">
                    {[
                        { label: "Total Students (All Time)", value: invoiceTotals.TotalStudents, color: "primary", icon: "fa-users", isCount: true },
                        { label: "Grand Total Billed", value: invoiceTotals.GrandTotalBilled, color: "info", icon: "fa-file-invoice" },
                        { label: "Grand Total Collected", value: invoiceTotals.GrandTotalCollected, color: "success", icon: "fa-money-bill-wave" },
                        { label: "Grand Outstanding", value: invoiceTotals.GrandTotalOutstanding, color: "danger", icon: "fa-exclamation-circle" },
                    ].map((c, i) => (
                        <div className="col-md-3" key={i}>
                            <div className={`card bg-light-${c.color}`}>
                                <div className="card-body py-4">
                                    <div className="d-flex align-items-center">
                                        <div className="symbol symbol-50px me-3">
                                            <span className={`symbol-label bg-${c.color}`}>
                                                <i className={`fa ${c.icon} text-white fs-3`}></i>
                                            </span>
                                        </div>
                                        <div>
                                            <div className="fs-5 fw-bold">{c.isCount ? (c.value || 0) : currencyConverter(c.value || 0)}</div>
                                            <div className="text-muted small">{c.label}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Invoice Status Breakdown + Collection Rate */}
                <div className="row g-4 mb-4">
                    <div className="col-md-8">
                        <div className="card h-100">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Invoice Status Breakdown</h3>
                            </div>
                            <div className="card-body py-3">
                                <div className="row g-3">
                                    {[
                                        { label: "Fully Paid", value: invoiceTotals.FullyPaidCount, color: "success" },
                                        { label: "Partially Paid", value: invoiceTotals.PartialCount, color: "warning" },
                                        { label: "Unpaid", value: invoiceTotals.UnpaidCount, color: "danger" },
                                        { label: "Total Invoices", value: invoiceTotals.TotalInvoices, color: "primary" },
                                    ].map((s, i) => (
                                        <div className="col-md-3" key={i}>
                                            <div className={`card border-${s.color}`}>
                                                <div className="card-body py-3 text-center">
                                                    <div className={`fs-3 fw-bold text-${s.color}`}>{s.value || 0}</div>
                                                    <div className="text-muted small">{s.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card h-100">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Overall Collection Rate</h3>
                            </div>
                            <div className="card-body d-flex align-items-center justify-content-center">
                                <div className="text-center">
                                    <div className="fs-1 fw-bold text-success">{collectionRate()}%</div>
                                    <div className="text-muted">of total billed collected</div>
                                    <div className="progress mt-3" style={{ height: 12 }}>
                                        <div className="progress-bar bg-success" style={{ width: `${collectionRate()}%` }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Channel Breakdown */}
                <div className="card mb-4">
                    <div className="card-header border-0 pt-5">
                        <h3 className="card-title">Payment Channel Breakdown (All Time)</h3>
                        <div className="card-toolbar">
                            <span className="text-muted fs-7">{paymentTotals.TotalTransactions || 0} transactions</span>
                        </div>
                    </div>
                    <div className="card-body py-3">
                        <div className="row g-3">
                            {[
                                { label: "Card Payments", value: paymentTotals.CardTotal, color: "primary", icon: "fa-credit-card" },
                                { label: "Bank Transfers", value: paymentTotals.BankTotal, color: "info", icon: "fa-university" },
                                { label: "Wallet Payments", value: paymentTotals.WalletTotal, color: "warning", icon: "fa-wallet" },
                                { label: "Mixed Payments", value: paymentTotals.MixedTotal, color: "secondary", icon: "fa-layer-group" },
                            ].map((ch, i) => (
                                <div className="col-md-3" key={i}>
                                    <div className={`card border-${ch.color}`}>
                                        <div className="card-body py-4 text-center">
                                            <i className={`fa ${ch.icon} fs-2 text-${ch.color} mb-2`}></i>
                                            <div className="fs-5 fw-bold">{currencyConverter(ch.value || 0)}</div>
                                            <div className="text-muted small">{ch.label}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Semester Trend Table */}
                <div className="card">
                    <div className="card-header border-0 pt-5">
                        <h3 className="card-title">Semester Trend</h3>
                        <div className="card-toolbar">
                            <button className="btn btn-sm btn-light-primary me-2" onClick={exportToCSV} disabled={!semesterRows.length}>
                                <i className="fa fa-download me-1"></i>Export CSV
                            </button>
                            <span className="text-muted fs-7">{semesterRows.length} semesters</span>
                        </div>
                    </div>
                    <div className="card-body py-3">
                        {semesterRows.length > 0 ? <AGTable data={datatable} /> : (
                            <div className="text-center py-10"><p className="text-muted">No semester data available</p></div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default connect()(UniversityWide);
